import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {CopilotGenerateDescriptionPanel} from '@github-ui/copilot-generate-description-panel'
import {useCopilotGeneratePanel} from '@github-ui/copilot-generate-description-panel/use-copilot-generate-panel'
import {validateIssueBody} from '@github-ui/entity-validators'
import type {ViewMode} from '@github-ui/markdown-editor'
import {testIdProps} from '@github-ui/test-id-props'
import {ScopedCommands} from '@github-ui/ui-commands'
import {AlertIcon, PencilAiIcon} from '@primer/octicons-react'
import {Button, Flash} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {type RefObject, useEffect, useId, useRef, useState} from 'react'

import {LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import styles from './IssueBodyEditor.module.css'

export type IssueBodyEditorProps = {
  subjectId: string
  subject: Subject
  body: string
  bodyIsStale?: boolean
  onChange: (newMarkdown: string) => void
  onCancel: (reset: boolean, returnRef?: RefObject<CommentBoxHandle | null>) => void
  onCommit: (subjectId: string) => void
  editorDisabled?: boolean
  trailingIcon?: boolean
  repoOwner?: string
  repoName?: string
  title?: string
  shouldShowCopilotFunctionality?: boolean
}

export function IssueBodyEditor({
  subjectId,
  subject,
  body,
  bodyIsStale,
  onChange,
  onCancel,
  onCommit,
  editorDisabled = false,
  trailingIcon = true,
  repoOwner,
  repoName,
  title = '',
  shouldShowCopilotFunctionality = false,
}: IssueBodyEditorProps) {
  const [validationError, setValidationError] = useState<string | undefined>(undefined)
  const [viewMode, setViewMode] = useState<ViewMode>()
  const ref = useRef<CommentBoxHandle>(null)

  // Use the Copilot panel hook to manage panel state and behavior
  const {
    isPanelOpen,
    isGenerating,
    shouldShowCopilot,
    togglePanel,
    handleInputFocus,
    commands: copilotPanelCommands,
    panelProps,
    containerStyle,
  } = useCopilotGeneratePanel({
    editorRef: ref,
    body,
    onChange,
    shouldShowCopilotFunctionality,
    repoOwner,
    repoName,
    title,
    generateTitle: false,
    commandId: 'issue-body:open-copilot-panel',
    canToggle: viewMode !== 'preview',
  })

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus()
    }
  }, [ref])

  const validationResult = validateIssueBody(body)

  const validationErrorId = useId()
  return (
    <ScopedCommands commands={copilotPanelCommands}>
      <div className={styles.editorContainer} style={containerStyle}>
        <CommentBox
          disabled={editorDisabled || isGenerating}
          saveButtonTrailingIcon={trailingIcon}
          subject={subject}
          value={body}
          placeholder={LABELS.issueBodyPlaceholder}
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          onChange={(newMarkdown: string) => {
            if (validationError) {
              setValidationError(undefined)
            }
            onChange(newMarkdown)
          }}
          onCancel={() => onCancel(true, ref)}
          validationResult={{isValid: true}}
          onSave={() => {
            if (validationResult.isValid) {
              onCommit(subjectId)
            } else {
              setValidationError(validationResult.errorMessage)
            }
          }}
          contentIsStale={bodyIsStale}
          ref={ref}
          label={'Body input'}
          aria-describedby={validationError ? validationErrorId : undefined}
          buttonSize="medium"
          teamHovercardsEnabled
          minHeightLines={isGenerating ? 8 : undefined}
          toolbarButtons={
            shouldShowCopilot ? (
              <Button
                className={styles.copilotWriteButton}
                variant="invisible"
                leadingVisual={PencilAiIcon}
                size="small"
                onClick={togglePanel}
                aria-pressed={isPanelOpen}
                aria-label={LABELS.copilotCTAButton}
              >
                <span className={styles.copilotWriteButtonText}>{LABELS.copilotCTAButton}</span>
              </Button>
            ) : null
          }
          generatingText={isGenerating}
          onInputFocus={handleInputFocus}
          {...testIdProps(TEST_IDS.commentBox('body'))}
        />
        {shouldShowCopilot && isPanelOpen && viewMode !== 'preview' && (
          <CopilotGenerateDescriptionPanel {...panelProps} />
        )}
      </div>
      {validationError && (
        <Flash variant="danger" id={validationErrorId} className={styles.validationErrorFlash}>
          <Octicon icon={AlertIcon} />
          {validationError}
        </Flash>
      )}
    </ScopedCommands>
  )
}

try{ IssueBodyEditor.displayName ||= 'IssueBodyEditor' } catch {}